import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, HeadlineL, LabelS } from '@sorare/core/src/atoms/typography';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import {
  mediumDesktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { useIsUserProEligible } from 'hooks/useIsUserProEligible';

import { TodaysCompetition_Event } from './__generated__/index.graphql';

const Wrapper = styled(Vertical)<{ background: string }>`
  position: relative;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-image: url(${props => props.background});

  width: 100%;
  margin: auto;
  text-align: center;
  align-items: center;
  padding: var(--double-unit);

  @media ${tabletAndAbove} {
    max-width: 1400px;
    padding: var(--double-unit) var(--quadruple-unit);
  }

  @media ${mediumDesktopAndAbove} {
    border-radius: var(--intermediate-unit);
  }

  & > * {
    z-index: 1;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to top,
      rgba(var(--c-rgb-black), 0.8),
      rgba(var(--c-rgb-black), 0)
    );
    pointer-events: none;
  }
`;

const Header = styled(LabelS)`
  border-radius: 40px;
  padding: var(--unit);
  backdrop-filter: blur(32px);
  background-color: rgba(var(--c-rgb-white), 0.1);
`;

type Props = {
  event: TodaysCompetition_Event;
};

export const TodaysCompetition = ({ event }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const { isProEligible } = useIsUserProEligible();

  const background = isTabletAndAbove
    ? event.desktopBanner
    : event.mobileBanner;

  if (!isProEligible) return null;
  return (
    <Wrapper background={background} gap={1}>
      <Header bold>
        <FormattedMessage
          id="TodaysCompetition.header.dailyhoops"
          defaultMessage="Today's Daily Hoops"
        />
      </Header>
      <HeadlineL>{event.title}</HeadlineL>
      <BodyS className="text-center" color="var(--c-nd-700)">
        {event.description}
      </BodyS>
    </Wrapper>
  );
};

TodaysCompetition.fragments = {
  Event: gql`
    fragment TodaysCompetition_Event on Event {
      title
      description
      mobileBanner: bannerUrl(large: false)
      desktopBanner: bannerUrl(large: true)
    }
  ` as TypedDocumentNode<TodaysCompetition_Event>,
};
